<template>
    <div>
        <div class="h-84 relative">
            <div @click="selectAction('edit')"
                 class="bg-white hover:bg-primary-5 trans-ease cursor-pointer"
            >
                <div class="h-84 flex items-center justify-between">
                    <div class="flex items-center">
                        <avatar
                            class="ml-24 mr-12"
                            :color="application.stageColor"
                            :image="application.image_url"
                        >
                            {{ application.initials }}
                        </avatar>
                        <div>
                            <span class="flex items-center leading-none gap-8">
                                <span class="text-black font-semibold text-body">{{ application.fullName }}</span>
                                <span class="text-neutral-grey text-body-sm">{{ interview.name }}</span>
                            </span>
                            <span class="text-body-sm text-black">{{ jobTitle }}</span>
                        </div>
                    </div>
                    <div class="flex gap-12">
                        <div class="flex items-center">
                            <div v-if="interview.subscriptions.length" class="flex">
                                <div class="flex mr-16">
                                    <tooltip
                                        v-for="sub in interview.subscriptions.filter(sub => sub.required && sub.user)"
                                        :key="sub.user.id"
                                        :content="sub.user.name"
                                        class="-mr-8 w-32 h-32"
                                        position="bottom"
                                    >
                                        <avatar
                                            :image="sub.user.image_url"
                                            size="small"
                                            class="cursor-default border border-white"
                                        >{{ sub.user.initials }}
                                        </avatar>
                                    </tooltip>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center">
                            <tooltip
                                v-if="interview.status"
                                wrapperClass="inline-block"
                                :content="interview.status.name"
                                position="right"
                            >
                                <svg-icon class="w-18 h-18" :class="'fill-' + interview.status.color" :type="interview.status.icon" />
                            </tooltip>
                        </div>
                        <div class="w-128">
                            <div class="text-center">
                            <p class="flex flex-col" v-if="interview.starts_at">
                                <span>{{ interviewDateTime }}</span>
                                <span>{{ interviewConfirmedTime }}</span>
                            </p>
                            <p class="flex flex-col" v-else-if="isWaitingForResponse">
                                <span>{{ firstTimeslotDate }}</span>
                                <span>{{ firstTimeslotTime }}</span>
                                <span v-if="interview.timeslots.length > 1"
                                      class="italic">
                                    {{ `(+${interview.timeslots.length - 1} ${$t('general.other').toLowerCase()})` }}
                                </span>
                            </p>
                            <span v-else class="italic">
                                {{ $t('general.does_not_apply') }}
                            </span>
                            </div>
                        </div>
                        <div class="pointer-events-auto" @click.stop>
                            <simple-dropdown
                                class="mr-24"
                                ref="ddmActions"
                                position="right"
                            >
                                <template #trigger>
                                    <button role="button">
                                        <svg-icon class="w-20 h-20 text-primary-80 group-hover:text-primary" type="more-horiz"/>
                                    </button>
                                </template>
                                <template #content>
                                    <nav
                                        aria-orientation="vertical"
                                        class="bg-white relative rounded shadow"
                                        role="menu"
                                    >
                                        <dropdown-item
                                            :disabled="!can.update"
                                            icon="edit"
                                            :input-label="interview.starts_at ? $t('interview.reschedule.title_short') : $t('general.edit')"
                                            @click="selectAction(interview.starts_at ? 'reschedule' : 'edit')"
                                        />
                                        <dropdown-item
                                            icon="mail"
                                            :input-label="$t('general.send_mail')"
                                            @click="selectAction('send-mail')"
                                        />
                                        <dropdown-item
                                            icon="person"
                                            :input-label="$t('general.go_to_application')"
                                            @click="selectAction('application')"
                                        />
                                        <dropdown-item
                                            danger
                                            :disabled="!can.delete"
                                            icon="delete"
                                            :input-label="[6, 7].includes(interview.status.id) ? $t('general.delete') : $t('general.cancel')"
                                            @click="selectAction('delete')"
                                        />
                                    </nav>
                                </template>
                            </simple-dropdown>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <interview-modal
            v-if="editing"
            :interview="interview"
            :application="interview.application"
            :can-manage="can.update"
            ref="interviewModal"
            @update="editInterview($event)"
            @close="stopEdit"
        ></interview-modal>

        <interview-cancel
            v-if="can.update && cancelingInterview"
            v-model="cancellationMail"
            :loading="performingDeleteRequest"
            :send-mail="sendCancellationMail"
            @changeSendMail="sendCancellationMail = $event"
            @close="stopCancel"
            @confirm="cancelInterview($event)"
        />

        <popup-modal-confirm
            v-if="can.delete && deleting"
            :confirmText="$t('interview.delete').toString()"
            :message="$t('interview.confirm_delete').toString()"
            :working="performingDeleteRequest"
            @close="stopDelete"
            @confirm="deleteInterview(deleting)"
        />

        <mail-creator
            v-if="sendingMail"
            context="single"
            :applications="[application]"
            :total-applications="1"
            @close="cancelEmail"
        ></mail-creator>
    </div>
</template>

<script>
import Avatar from "../ui/Avatar.vue";
import Interview from "../../models/Interview";
import SvgIcon from "../ui/SvgIcon.vue";
import Tooltip from "../navigation/Tooltip.vue";
import moment from "moment";
import {mapState} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import SimpleDropdown from "../ui/SimpleDropdown.vue";
import InterviewModal from "../Interview.vue";
import PopupModalConfirm from "../ui/PopupModalConfirm.vue";
import InterviewCancel from "../InterviewCancel.vue";
import * as interviews from "../../services/InterviewService";
import {normalizeJson} from "../../../shared/helpers";
import MailCreator from "../ui/MailCreator.vue";
import DropdownItem from "../ui/DropdownItem.vue";

export default {
    name: 'InterviewListItem',
    components: {
        DropdownItem,
        MailCreator,
        InterviewCancel, PopupModalConfirm, InterviewModal, SimpleDropdown, Tooltip, SvgIcon, Avatar},
    props: {
        interview: Interview,
    },
    data() {
        return {
            sendingMail: false,
            fetchingStatuses: false,
            editing: false,
            deleting: false,
            cancelingInterview: false,
            performingDeleteRequest: false,
            sendCancellationMail: true,
            cancellationMail: {
                attachments: [],
                subject: '',
                message: ''
            },
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        can() {
            return {
                update: this.$can(['applications.manage', 'applications.manage.' + this.application.id, 'jobs.manage.' + this.application.stage.job.id]),
                delete: this.$can(['applications.manage', 'applications.manage.' + this.application.id, 'jobs.manage.' + this.application.stage.job.id]),
            };
        },
        application() {
            return this.interview.application;
        },
        createdAt() {
            return moment(this.interview.created_at).format('DD MMMM YYYY');
        },
        jobTitle() {
            return this.interview.application.stage.job.title[this.locale];
        },
        isSubscribed() {
            return this.interview.application.subscribed;
        },
        isWaitingForResponse() {
            return this.interview.status.id === 1;
        },
        firstTimeslotDate() {
            let first = this.interview.timeslots[0];
            if (!first) return;
            return moment(first.starttime).locale(this.locale).format('LL');
        },
        firstTimeslotTime() {
            let first = this.interview.timeslots[0];
            if (!first) return;
            return `${moment(first.starttime).locale(this.locale).format('LT')} -
                ${moment(first.endtime).locale(this.locale).format('LT')}`;
        },
        interviewDateTime() {
            return moment(this.interview.starts_at).locale(this.locale).format('LL')
        },
        interviewConfirmedTime() {
            return `${moment(this.interview.starts_at).locale(this.locale).format('LT')} -
            ${moment(this.interview.starts_at).add(this.interview.duration, 'minutes').locale(this.locale).format('LT')}`;
        },
    },
    methods: {
        cancelEmail() {
            this.sendingMail = false;
        },
        async selectAction(action) {
            this.$refs.ddmActions.closeMe();

            if (action === 'edit') {
                this.editing = true;
            }

            if (action === 'send-mail') {
                this.sendingMail = true;
            }

            if (action === 'application') {
                await this.$router.push({name: 'application', params: {id: this.application.id}});
            }

            if (action === 'reschedule') {
                this.editing = true;
                this.$nextTick(() => {
                    this.$refs.interviewModal?.rescheduleHandler();
                })
            }

            if (action === 'delete') {
                if (!this.interview.status || ![6, 7].includes(this.interview.status.id) && !this.interview.instant) {
                    this.cancelingInterview = this.interview;
                } else {
                    this.deleting = this.interview;
                }
            }
        },
        editInterview(interview) {
            this.showInterview = true;
            this.editingInterview = interview;
        },
        stopEdit() {
            this.editing = false;
            this.deleting = false;
            this.showInterview = false;
            this.editingInterview = null;
        },
        stopCancel() {
            this.cancelingInterview = null;
            this.cancellationMail = {
                attachments: [],
                subject: '',
                message: ''
            };
            this.performingDeleteRequest = false;
        },
        stopDelete() {
            this.deleting = null;
            this.performingDeleteRequest = false;
        },
        cancelInterview(signature) {
            this.performingDeleteRequest = true;

            // add signature if present
            if (signature) {
                this.cancellationMail.message += signature;
            }

            interviews.cancel(this.cancelingInterview.id, {
                data: {
                    attributes: {
                        subject: this.cancellationMail.subject,
                        message: this.cancellationMail.message,
                    },
                    options: {
                        send_mail: this.sendCancellationMail,
                    }
                },
            }).then(result => {
                return useRepo(Interview)
                    .whereId(result.data.id)
                    .update(normalizeJson(result.data));
            }).then(() => {
                this.stopCancel()
            });
        },
        deleteInterview(interview) {
            this.performingDeleteRequest = true;

            interviews.destroy(interview.id).then(() => {
                useRepo(Interview).destroy(interview.id);
            }).then(() => {
                this.stopDelete();
            });
        },
    },
}
</script>
